import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Boxes, Truck } from "lucide-react";
import { BAG_MACROS, FAN_FAVOURITE, FLAVOURS, FREE_SHIP_MIN, REVIEWS } from "@/data/content";
import { money, perBagMoney, useShop } from "@/lib/shop";
import { findSixPack } from "@/components/site/Shop";
import { AddButton } from "@/components/site/AddButton";
import { ProductInfo } from "@/components/site/ProductInfo";
import { BagArt } from "@/components/site/BagArt";
import { FanBadge } from "@/components/site/FanBadge";
import { Reveal, Eyebrow } from "@/components/site/Reveal";

const useSeo = (f) => {
  useEffect(() => {
    const prev = document.title;
    document.title = `${f.name} Protein Gummies 6 Pack | Swole Bears`;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    meta?.setAttribute("content", `${f.name} protein gummy bears: ${f.notes.toLowerCase()}. ${BAG_MACROS.protein}g protein per bag and 75% less sugar than the leading candy equivalent. Made in Nova Scotia.`);
    return () => { document.title = prev; if (meta && prevDesc) meta.setAttribute("content", prevDesc); };
  }, [f]);
};

const Gallery = ({ f }) => {
  const slides = [
    { key: "bag", label: "Bag" },
    { key: "facts", label: "Nutrition", src: "/img/nutrition.jpg" },
  ];
  const [idx, setIdx] = useState(0);
  return (
    <div data-testid="flavour-gallery">
      <div className="relative aspect-square lg:aspect-[6/5] rounded-[32px] overflow-hidden" style={{ background: `${f.color}2E` }}>
        {f.isNew && <span className="absolute top-5 left-5 z-10 rounded-full bg-[#FF5A5F] text-white text-xs font-bold uppercase px-3 py-1">New flavour</span>}
        {f.key === FAN_FAVOURITE && <FanBadge className="absolute top-5 right-5 z-10" />}
        {slides.map((s, i) => (
          <div key={s.key} className={`absolute inset-0 p-8 sm:p-12 flex items-center justify-center transition-opacity duration-500 ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
            {s.key === "bag"
              ? <motion.div animate={{ y: [0, -12, 0], rotate: [-2, 2, -2] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} className="h-full flex items-center"><BagArt f={f} className="h-full max-w-full drop-shadow-[0_30px_40px_rgba(27,21,48,0.3)]" /></motion.div>
              : <img src={s.src} alt={`${f.name} ${s.label}`} className={`h-full w-full rounded-2xl ${s.key === "facts" ? "object-contain bg-white p-3" : "object-cover"}`} />}
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2" role="tablist">
        {slides.map((s, i) => (
          <button key={s.key} type="button" onClick={() => setIdx(i)} aria-selected={i === idx} data-testid={`flavour-gallery-${s.key}`}
            className={`h-10 px-4 rounded-full text-sm font-bold transition-colors ${i === idx ? "bg-[#1B1530] text-white" : "bg-[#1B1530]/5 hover:bg-[#1B1530]/10"}`}>{s.label}</button>
        ))}
      </div>
    </div>
  );
};

const Macro = ({ v, label }) => (
  <div className="rounded-2xl bg-white border border-[#1B1530]/10 px-4 py-3">
    <p className="font-display text-2xl sm:text-3xl leading-none">{v}</p>
    <p className="mt-1 text-xs font-semibold text-[#4A4560]">{label}</p>
  </div>
);

const BuyBox = ({ f, product, refPrice }) => {
  const price = product?.price ?? refPrice;
  return (
    <div>
      <Eyebrow className="text-[#FF5A5F]">Protein gummies · 6 bags</Eyebrow>
      <h1 data-testid="flavour-page-title" className="mt-3 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.92]">{f.name}</h1>
      <p className="mt-4 text-base sm:text-lg text-[#4A4560]">{f.notes}. Soft, chewy and sweetened with honey, with {BAG_MACROS.protein}g of protein in every bag.</p>
      <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-3">
        <Macro v={`${BAG_MACROS.protein}g`} label="protein per bag" />
        <Macro v="75%" label="less sugar vs leading candy" />
        <Macro v={BAG_MACROS.calories} label="calories per bag" />
      </div>
      <div className="mt-8 flex items-end justify-between gap-4">
        <div>
          <p data-testid="flavour-page-price" className="font-display text-4xl">{price ? money(price) : <span className="inline-block h-9 w-32 rounded-lg bg-[#1B1530]/10 animate-pulse align-middle" />}</p>
          {price && <p className="text-sm font-semibold text-[#4A4560]">{perBagMoney(price / 6)}/bag · 6 x 70g bags</p>}
        </div>
      </div>
      <AddButton product={product} image={f.img} comingSoon={!product} testid="flavour-page-add-button" className="mt-5 w-full h-14 text-base" />
      <Link to="/build?size=12" data-testid="flavour-page-build-link" className="mt-3 flex items-center justify-center gap-2 h-12 rounded-full border-2 border-[#1B1530] font-bold hover:bg-[#1B1530] hover:text-white transition-colors">
        <Boxes className="h-4 w-4" /> Or mix it into a box and save
      </Link>
      <p className="mt-4 flex items-center gap-2 text-sm text-[#4A4560]"><Truck className="h-4 w-4 shrink-0" /> Free shipping on orders over ${FREE_SHIP_MIN}.</p>
      <ProductInfo flavourKeys={[f.key]} className="mt-5" />
    </div>
  );
};

const OtherFlavours = ({ current }) => (
  <section data-testid="flavour-page-others" className="py-16 lg:py-24">
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
      <h2 className="font-display text-3xl sm:text-4xl">Try the other flavours</h2>
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {FLAVOURS.filter((x) => x.key !== current).map((x, i) => (
          <Reveal key={x.key} delay={i * 0.06}>
            <Link to={`/flavours/${x.key}`} data-testid={`other-flavour-${x.key}`} className="group block rounded-[24px] p-4 sm:p-6 transition-transform duration-300 hover:-translate-y-1" style={{ background: `${x.color}2E` }}>
              <BagArt f={x} className="h-40 sm:h-52 mx-auto transition-transform duration-500 group-hover:-rotate-3" />
              <p className="mt-3 font-display text-lg sm:text-xl leading-tight flex items-center justify-between gap-2">{x.name} <ArrowRight className="h-4 w-4 shrink-0" /></p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Love = () => (
  <section data-testid="flavour-page-reviews" className="py-16 lg:py-20 bg-[#1B1530] text-white">
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
      <Eyebrow className="text-[#FFB21A]">What people say</Eyebrow>
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        {REVIEWS.slice(0, 3).map((r) => (
          <figure key={r.name} className="rounded-[24px] bg-white/5 border border-white/10 p-6">
            <blockquote className="text-zinc-200">"{r.text}"</blockquote>
            <figcaption className="mt-4 font-bold">{r.name}</figcaption>
          </figure>
        ))}
      </div>
      <Link to="/reviews" data-testid="flavour-page-reviews-link" className="mt-8 inline-flex items-center gap-2 font-bold text-[#FFB21A] hover:underline">Read all reviews <ArrowRight className="h-4 w-4" /></Link>
    </div>
  </section>
);

export default function FlavourPage() {
  const { key } = useParams();
  const f = FLAVOURS.find((x) => x.key === key);
  const { products } = useShop();
  useSeo(f || FLAVOURS[0]);
  if (!f) return <Navigate to="/shop" replace />;
  const product = findSixPack(products, f);
  const refPrice = FLAVOURS.map((x) => findSixPack(products, x)?.price).find(Boolean);

  return (
    <main data-testid="flavour-page">
      <section className="pt-36 sm:pt-40 pb-12">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <Link to="/shop" data-testid="flavour-page-back" className="inline-flex items-center gap-2 text-sm font-bold text-[#4A4560] hover:text-[#1B1530]"><ArrowLeft className="h-4 w-4" /> All flavours</Link>
          <div className="mt-6 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-7"><Gallery f={f} /></div>
            <div className="lg:col-span-5 lg:sticky lg:top-36"><BuyBox f={f} product={product} refPrice={refPrice} /></div>
          </div>
        </div>
      </section>
      <Love />
      <OtherFlavours current={f.key} />
    </main>
  );
}
